import { 
  collection, 
  getDocs, 
  writeBatch, 
  doc, 
  updateDoc,
  query,
  where,
  limit
} from "firebase/firestore";
import { legacyDb, db, auth } from "../firebaseConfig";
import { ReportTemplate, Phrase, Category } from "../types";

export interface MigrationResult {
  reportsMigrated: number;
  phrasesMigrated: number;
  categoriesMigrated: number;
  error?: string;
}

/**
 * Migrates user data from USA Legacy database to European Default database.
 */
export const migrateUserDataFromUS = async (userId: string): Promise<MigrationResult> => {
  const result: MigrationResult = {
    reportsMigrated: 0,
    phrasesMigrated: 0,
    categoriesMigrated: 0
  };

  console.log(`[Migration] Starting rescue for user: ${userId} (${auth.currentUser?.email})`);
  console.log(`[Migration] Searching in USA Project ID: ${legacyDb.app.options.projectId}`);

  try {
    const batch = writeBatch(db);

    // 1. Migrate Reports (Checking subcollection and legacy root collection by UID AND Email)
    const reportSources = [
      collection(legacyDb, "users", userId, "reports"),
      query(collection(legacyDb, "reports"), where("userId", "==", userId)),
      query(collection(legacyDb, "reports"), where("userEmail", "==", auth.currentUser?.email))
    ];

    // DIAGNOSTIC LOGS: List sample data from USA
    try {
      const diagSnap = await getDocs(query(collection(legacyDb, "reports"), limit(2)));
      console.log(`[Migration-Diag] Sample documents in USA 'reports' root:`, diagSnap.docs.map(d => ({id: d.id, keys: Object.keys(d.data())})));
    } catch (err) {
      console.warn("[Migration-Diag] Could not run diagnostic query:", err);
    }

    for (const source of reportSources) {
      try {
        const snap = await getDocs(source);
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const targetRef = doc(db, "users", userId, "reports", docSnap.id);
          batch.set(targetRef, {
            ...data,
            migratedFrom: "USA",
            migratedAt: Date.now()
          }, { merge: true });
          result.reportsMigrated++;
        });
      } catch (e) {
        console.warn("[Migration] Failed to read one report source:", e);
      }
    }

    // 2. Migrate Phrases
    const phraseSources = [
      collection(legacyDb, "users", userId, "phrases"),
      query(collection(legacyDb, "phrases"), where("userId", "==", userId))
    ];

    for (const source of phraseSources) {
      try {
        const snap = await getDocs(source);
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const targetRef = doc(db, "users", userId, "phrases", docSnap.id);
          batch.set(targetRef, {
            ...data,
            migratedFrom: "USA",
            migratedAt: Date.now()
          }, { merge: true });
          result.phrasesMigrated++;
        });
      } catch (e: any) {
        console.warn("[Migration] Failed to read one phrase source:", e);
        if (e.code === "permission-denied") {
          result.error = "Error de permisos en USA database.";
        }
      }
    }

    // 3. Migrate Categories & Filters
    const catSources = [
      collection(legacyDb, "users", userId, "categories"),
      collection(legacyDb, "users", userId, "filters")
    ];

    for (const source of catSources) {
      try {
        const snap = await getDocs(source);
        snap.forEach((catDoc) => {
          batch.set(doc(db, "users", userId, "categories", catDoc.id), {
            ...catDoc.data(),
            migratedFrom: "USA",
            migratedAt: Date.now()
          }, { merge: true });
          result.categoriesMigrated++;
        });
      } catch (e) {
        console.warn("[Migration] Categories/Filters source failed:", e);
      }
    }

    if (result.reportsMigrated === 0 && result.phrasesMigrated === 0 && result.categoriesMigrated === 0) {
      throw new Error(result.error || "No se localizaron datos para rescatar en la base de datos de USA.");
    }

    // 4. Update Profile Status in EU Database
    const profileRef = doc(db, "users", userId, "profile", "main");
    batch.update(profileRef, {
      dataRescueStatus: 'COMPLETED'
    });

    await batch.commit();
    console.log("[Migration] Rescue completed successfully:", result);
    return result;

  } catch (error: any) {
    console.error("Migration error:", error);
    return { ...result, error: error.message || "Error desconocido durante el rescate" };
  }
};

/**
 * Mark migration as dismissed/ignored
 */
export const dismissMigration = async (userId: string) => {
  try {
    const profileRef = doc(db, "users", userId, "profile", "main");
    await updateDoc(profileRef, {
      dataRescueStatus: 'DISMISSED'
    });
  } catch (error) {
    console.error("Error dismissing migration:", error);
  }
};
